<template>
  <div
    class="practice-hands-totals prahndsttls_filter"
    :class="[{ prahndsttls_error: stats_call.error }, { prahndsttls_loading: stats_call.loading }]"
  >
    <GwFilter
      v-if="hasFilters"
      :asRow="true"
      :hasSaveBtn="false"
      :hasClearBtn="false"
      :filterUrlPrefix="filterUrlPrefix"
      :filterConfig="filterConfig"
      class="mb-2"
    />

    <div class="prahndsttls_filter_content">
      <slot
        name="content"
        :apiCall="stats_call"
        :data="data"
        :types="types"
        :totals="totals"
      >
      </slot>
    </div>
  </div>
</template>

<script>
import GwFilter from '@/components/DataTables/Filters/new/GwFilter'
import { getCorrectnessActionGroups } from '@/helpers/constants'
import { getTrainerScoreGroup } from '@/helpers/trainer'
import { getPercentageOfMax } from '@/helpers/helpers'

import format_mix from '@/mixins/format_mix'
import fetch_mix from '@/mixins/fetch_mix'

export default {
  name: 'PracticeHandsTotalsFilter',
  components: {
    GwFilter,
  },
  mixins: [format_mix, fetch_mix],
  props: {
    hasFilters: { type: Boolean, default: true },
    filterConfig: Object,
    filterUrlPrefix: { type: String, default: () => 'ffxs' },
    endpoint: { type: String, default: () => 'poker/practice/practice-hands/totals/' },
    callConfig: {
      default: () => ({
        apiVersion: 'v1',
      }),
    },
  },
  data() {
    return {
      callConf: {},
      stats_call: {
        conf: {},
        loading: false,
        loaded: false,
        error: null,
        canceled: false,
        status: 0,
        hasCancel: false,
      },
      data: {
        totals: {},
        by_types: [],
      },
    }
  },
  methods: {
    async getData() {
      const confBase = {
        endpoint: this.endpoint,
        ...this.callConfig,
      }
      const conf = !this.hasFilters
        ? confBase
        : !this.filter.loaded
          ? {}
          : {
              ...confBase,
              params: this.params,
            }
      if (JSON.stringify(conf) === JSON.stringify(this.callConf)) {
        return
      }
      this.callConf = conf
      this.stats_call.conf = conf
      const { data, error } = await this.fch_getData(this.stats_call)
      if (error) {
        console.log(error)
        return
      }
      // console.log({ call, data, error })
      this.data = data || { totals: {}, by_types: [] }
    },
    getBoxData({ id, data, skeleton }) {
      const groups = getCorrectnessActionGroups()

      let moves = [
        {
          name: this.$t(`practice.stats.bests`),
          id: 'best_moves_sum',
          unit: '',
          group: groups.best_move,
        },
        {
          name: this.$t(`practice.stats.corrects`),
          id: 'correct_moves_sum',
          unit: '',
          group: groups.correct_move,
        },
        {
          name: this.$t(`practice.stats.inaccuracy`),
          id: 'inaccuracy_moves_sum',
          unit: '',
          group: groups.inaccuracy,
        },
        {
          name: this.$t(`practice.stats.wrongs`),
          id: 'wrong_moves_sum',
          unit: '',
          group: groups.wrong_move,
        },
        {
          name: this.$t(`practice.stats.blunders`),
          id: 'blunder_moves_sum',
          unit: '',
          group: groups.blunder,
        },
      ]

      const movesValues = moves.map(dd => data[dd.id])
      const max = Math.max(...movesValues)
      moves = moves.map(dd => {
        const rr = {
          ...dd,
          name: dd.name,
          value: this.numKtoM(data[dd.id] || 0, 10000, 1),
          value_percent_scaled: getPercentageOfMax({
            max,
            value: data[dd.id],
          }),
        }
        return rr
      })

      let rows = [
        {
          id: 'total_ev_loss',
          rows: [
            {
              id: 'total_ev_loss_sum',
              unit: this.$t('common.bb'),
              decimal: 1,
            },
          ],
        },
        {
          id: 'average_ev_loss',
          rows: [
            {
              id: 'total_ev_loss_avg',
              unit: this.$t('common.bb_hand'),
              decimal: 2,
            },
            {
              id: 'total_ev_loss_as_pot_avg',
              unit: this.$t('common.pot_hand'),
              decimal: 2,
              times: 100,
            },
          ],
        },
        {
          id: 'average_mistake_ev_loss',
          rows: [
            {
              id: 'total_ev_loss_mistake_avg',
              unit: this.$t('common.bb_mistake'),
              decimal: 2,
            },
            {
              id: 'total_ev_loss_as_pot_mistake_avg',
              unit: this.$t('common.pot_mistake'),
              decimal: 2,
              times: 100,
            },
          ],
        },
        {
          id: 'total_frequency_difference',
          rows: [
            {
              id: 'total_frequency_difference_avg',
              unit: this.$t('common.percent_hand'),
              decimal: 0,
              times: 100,
            },
          ],
        },
      ]
      rows = rows.map(dd => {
        const rr = {
          ...dd,
          name: this.$t(`practice.stats.${dd.id}`),
          tooltip: this.$t(`tooltips.${dd.id}`),
          rows: dd.rows.map(ii => {
            const rrr = {
              ...ii,
              value: this.numFixed((data[ii.id] || 0) * (ii.times || 1), ii.decimal),
            }
            return rrr
          }),
        }
        return rr
      })

      const gto_score = {
        id: 'gto_score_avg',
        unit: '',
        value: this.numFixed((data.gto_score_avg || 0) * 100),
        group: getTrainerScoreGroup(data.gto_score_avg),
      }
      const total_moves = moves.reduce((a, b) => a + b.value, 0)

      const topBoxes = [
        {
          name: this.$t('common.hands'),
          value: this.numKtoM(data.total_hands || 0, 10000, 2),
        },
        {
          name: this.$t('common.moves'),
          value: this.numKtoM(data.played_moves_sum || 0, 10000, 2),
        },
        {
          name: this.$t('common.mistakes'),
          value: this.numKtoM(data.mistake_moves_sum || 0, 10000, 2),
        },
        {
          name: this.$t('common.gto_score'),
          value: gto_score.value || 0,
          unit: '%',
        },
      ]
      const res = {
        id,
        gto_score,
        topBoxes,
        disabled: !total_moves,
        skeleton,
        rows,
        moves,
      }
      // console.log(res)
      return res
    },
  },
  computed: {
    filter() {
      return this.$store.getters[this.filterUrlPrefix]
    },
    params() {
      return !this.filter.loaded
        ? {}
        : {
            ...(this.filter.params || {}),
          }
    },
    totals() {
      const data = this.data.totals
      const res = this.getBoxData({ id: 'totals', data, skeleton: false })
      res.gto_score_trend = this.data.gto_score_trend
      return res
    },
    types() {
      let res = ['SRP', '3bet', '4bet', '5bet', 'limp', 'iso']
      const { by_types } = this.data
      res = res.map(i => {
        let data = by_types.find(tt => tt.type === i)
        const skeleton = !data
        data = data || {}
        const r = this.getBoxData({ id: i, data, skeleton })
        return r
      })
      // console.log(res)
      return res
    },
  },
  watch: {
    params: {
      immediate: true,
      handler: 'getData',
    },
  },
}
</script>

<style lang="scss" scoped>
.prahndsttls_filter {
  display: flex;
  position: relative;
  flex-direction: column;
  &_filters {
    display: flex;
    flex-wrap: wrap;
    grid-gap: 1rem;
  }
  &_content {
    display: flex;
  }
}
</style>
